<script lang="ts" setup>
import { computed, useSlots } from 'vue';
import { BFormInput, BTable } from 'bootstrap-vue';
import { TableField, FieldType, Pagination, SortConfig } from '@/typings/dynamicTable';
import CButton from './CButton/CButton.vue';

const props = withDefaults(
    defineProps<{
        tableConfig: TableField<any>[];
        items?: unknown[];
        paginationData?: Pagination;
        loading?: boolean;
        customSearchAndPagination?: boolean;
        onRowClicked?: (row: unknown) => void;
        tBodyTrClass?: string;
        sort?: SortConfig;
    }>(),
    {
        paginationData: () => ({
            currentPage: 1,
            pageSize: 10,
            totalItemCount: 0
        })
    }
);

const emit = defineEmits<{
    (event: 'search', value: string): void;
    (event: 'page-change', page: number): void;
    (event: 'sort-mode-sets', e: MouseEvent): void;
    (event: 'sort-sets', setMode?: string): void;
}>();

const slots = useSlots();

const isClickActive = computed(() => {
    return !!props.onRowClicked;
});

const getSlotName = computed(() => {
    return (field: TableField) => `cell(${field.key})`;
});

const getHeaderSlotName = computed(() => {
    return (field: TableField) => `head(${field.key})`;
});

const executeAction = (field: TableField, item: Record<string, unknown>, event: MouseEvent) => {
    if (!field.action) return;
    event.stopPropagation();
    field.action(item);
};

const rowClicked = (item: unknown) => {
    props.onRowClicked?.(item);
};

const getRowsClasses = () => {
    const classes = ['custom-body'];
    if (isClickActive) classes.push('cursor-pointer');
    if (props.tBodyTrClass) classes.push(props.tBodyTrClass);
    return classes.join(' ');
};
</script>

<template>
    <div class="dynamic-table-wrapper">
        <div v-if="!customSearchAndPagination" class="d-flex justify-content-between align-items-center mb-3">
            <slot name="header" />
            <div class="d-flex justify-content-end ml-auto">
                <slot name="filter" />
                <b-form-input
                    class="ml-3"
                    type="search"
                    placeholder="Search"
                    debounce="400"
                    :value="paginationData.searchValue"
                    @update="emit('search', $event)"
                />
            </div>
        </div>
        <slot name="sub-header" />
        <slot />
        <b-table
            style="white-space: pre-line"
            class="w-100 table"
            responsive="sm"
            :fields="props.tableConfig"
            :items="props.items"
            :busy="props.loading"
            :hover="isClickActive"
            show-empty
            :tbody-tr-class="getRowsClasses"
            thead-tr-class="custom-header"
            empty-text="No items"
            empty-filtered-text="No items matching your search"
            @row-clicked="rowClicked"
        >
            <template v-for="(column, index) of tableConfig" v-slot:[getHeaderSlotName(column)]>
                <div
                    v-if="column.type === 'sort'"
                    :key="`${String(column.key)}-head${index}`"
                    class="sort-column-header"
                    @click="emit('sort-sets', column.sortKey)"
                >
                    <div class="header-sort-container">
                        {{ column.label }}
                        <CButton
                            v-if="sort?.sortBy === column.sortKey"
                            class="btn-dark-transparent"
                            size="sm"
                            @click="emit('sort-mode-sets', $event)"
                        >
                            <font-awesome-icon
                                v-if="sort?.sortMode === 'ASC'"
                                :icon="['fas', 'fa-sort-up']"
                                :has-fill="false"
                                class="up-arrow"
                            />
                            <font-awesome-icon
                                v-if="sort?.sortMode !== 'ASC'"
                                :icon="['fas', 'fa-sort-down']"
                                :has-fill="false"
                                class="down-arrow"
                            />
                        </CButton>
                    </div>
                </div>
            </template>
            <template v-for="(column, index) of tableConfig" v-slot:[getSlotName(column)]="{ field, item, value }">
                <!-- @vue-ignore -->
                <div
                    :key="`${column.key}-${index}`"
                    :class="{ 'deeper-hover': isClickActive && field.action && value }"
                    @click="executeAction(field, item, $event)"
                >
                    <span :class="{ 'actionable-item': field.action }">
                        <img
                            v-if="column.type == FieldType.Image"
                            class="img-thumbnail"
                            width="50"
                            alt="Preview"
                            :src="value"
                        />
                        <template v-else>
                            {{ value }}
                        </template>
                    </span>
                </div>
            </template>

            <template #table-busy>
                <div class="text-center text-primary my-2">
                    <b-spinner class="align-middle mr-2" />
                    <strong>Loading...</strong>
                </div>
            </template>

            <!-- override slots for cases where custom renders are needed -->
            <template v-for="(_, slotName, index) of slots" v-slot:[slotName]="scope">
                <div :key="`${slotName}-${index}`">
                    <slot :name="slotName" v-bind="scope" />
                </div>
            </template>
        </b-table>

        <div v-if="!customSearchAndPagination" class="d-flex justify-content-center">
            <b-pagination
                class="mb-0"
                size="md"
                :per-page="paginationData.pageSize"
                :total-rows="paginationData.totalItemCount"
                :value="paginationData.currentPage"
                @change="emit('page-change', $event)"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.header-sort-container {
    display: flex;
    align-items: center;

    & > button {
        padding: 0 0.55rem;
        height: 14px;
        display: flex;
        align-items: center;

        &:hover {
            background-color: unset !important;
        }

        &:focus {
            box-shadow: none;
        }
    }
}
.sort-column-header svg {
    justify-content: center !important;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    width: 14px;
    color: #333333;
    position: relative;
    &.up-arrow {
        top: 3px;
    }
    &.down-arrow {
        top: -3px;
    }
}

.dynamic-table-wrapper {
    .actionable-item {
        cursor: pointer;
        color: #3e6dc5;
        text-decoration: underline;
    }
    .table {
        font-size: 0.875rem;
        :deep() {
            .b-table {
                // fake height set in order to the
                // actionable div take the full height
                height: 1px;
                .custom-body {
                    &:hover {
                        background-color: #f0f2f4;
                    }
                    td {
                        vertical-align: middle;
                        &:has(.deeper-hover) {
                            padding: 0;
                        }
                        .deeper-hover {
                            display: flex;
                            align-items: center;
                            height: 100%;
                            padding: 0.75rem;
                            height: 100%;
                            &:hover {
                                background-color: #ebecef;
                                border: 0.25rem;
                            }
                        }
                    }
                }
                .cursor-pointer {
                    cursor: pointer;
                }
                .custom-header {
                    th {
                        border-top: unset;
                    }
                }
            }
        }
    }
}
</style>
